// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Alert, Anchor, Button, Container, Stack, Text, TextInput, Title } from '@mantine/core';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useState } from 'react';
import { getIntakeBotClient } from '../../lib/bot-client';
import { botIdentifier } from '../../lib/bot-identifier';

export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setLoading(true);
    try {
      const client = await getIntakeBotClient();
      await client.executeBot(botIdentifier('request-provider-password-reset'), { email }, 'application/json');
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  }

  if (submitted) {
    return (
      <Container size="xs" mt="xl">
        <Stack gap="md">
          <Title order={2}>Check your email</Title>
          <Alert color="blue">
            If an account exists for that email address, we&apos;ve sent a link to reset your password.
          </Alert>
          <Anchor href="/signin">Back to sign in</Anchor>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="xs" mt="xl">
      <Stack gap="md">
        <Title order={2}>Reset your password</Title>
        <Text c="dimmed" size="sm">
          Enter your email address and we&apos;ll send you a link to reset your password.
        </Text>
        <form onSubmit={handleSubmit}>
          <Stack gap="sm">
            <TextInput
              label="Email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
            />
            <Button type="submit" loading={loading} fullWidth>
              Send reset link
            </Button>
            <Anchor href="/signin" size="sm">
              Back to sign in
            </Anchor>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}
